import { useState, useEffect } from 'react';
import {
  Button, TextField, Dialog, DialogActions, DialogContent, 
  DialogTitle, FormControl, InputLabel, Select, MenuItem, 
  CircularProgress, Alert
} from '@mui/material';
import { 
  INDUSTRY_TYPES,
  CLIENT_LEVELS,
} from '@/constant/setting.ts';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar.tsx';
import { apiClient } from '@/services/api';

// TypeScript 接口定义
interface User {
  id: number;
  username: string;
  display_name?: string;
  role: string;
  org_id?: number;
}

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id?: number;
  report_template_id?: number;
  industry_type?: string;
  client_level?: string;
  evaluation_type?: string;
  status: string;
  contract_year?: number;
  dify_dataset_id?: string;
  created_at: string;
  updated_at: string;
}

interface NewCreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: () => void;
  editMode?: boolean;
  projectDataToEdit?: Project | null;
  projectType?: string; // 项目类型：key_project 或 performance_target
}

export default function NewCreateProjectDialog({
  open,
  onClose,
  onProjectCreated,
  editMode = false,
  projectDataToEdit = null,
  projectType = "key_project"
}: NewCreateProjectDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Form states
  const [projectName, setProjectName] = useState('');
  const [contractYear, setContractYear] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [clientLevel, setClientLevel] = useState('');

  useEffect(() => {
    // Get current user from localStorage
    try {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      } else {
        setError("无法获取当前用户信息，请重新登录。");
      }
    } catch (e) {
      setError("解析用户信息失败。");
    }

    if (open) {
      setError(null);
      setSubmitting(false);

      if (editMode && projectDataToEdit) {
        // --- 编辑模式 ---
        setProjectName(projectDataToEdit.project_name || '');
        setContractYear(projectDataToEdit.contract_year?.toString() || new Date().getFullYear().toString());
        setIndustryType(projectDataToEdit.industry_type || '');
        setClientLevel(projectDataToEdit.client_level || '');
      } else {
        // --- 创建模式 ---
        setProjectName('');
        setContractYear(new Date().getFullYear().toString());
        setIndustryType('');
        setClientLevel('');
      }
    }
  }, [open, editMode, projectDataToEdit]);

  // 验证合同年度是否有效
  const isValidContractYear = (year: string): boolean => {
    if (!year || !year.trim()) return true; // 空值视为有效（可选字段）
    
    const currentYear = new Date().getFullYear();
    const yearNum = parseInt(year);
    
    if (isNaN(yearNum)) return false;
    
    const minYear = currentYear - 10;
    const maxYear = currentYear + 10;
    
    return yearNum >= minYear && yearNum <= maxYear;
  };

  const validateForm = (): boolean => {
    if (!projectName.trim()) {
      setError('项目名称是必填项。');
      return false;
    }
    if (!industryType) {
      setError('必须选择一个行业类型。');
      return false;
    }
    if (!clientLevel) {
      setError('必须选择一个客户级别。');
      return false;
    }
    
    // 验证合同年度
    if (contractYear && contractYear.trim()) {
      const currentYear = new Date().getFullYear();
      const year = parseInt(contractYear);
      
      if (isNaN(year)) {
        setError('合同年度必须是有效的数字。');
        return false;
      }
      
      const minYear = currentYear - 10;
      const maxYear = currentYear + 10;
      
      if (year < minYear || year > maxYear) {
        setError(`合同年度必须在 ${minYear} 到 ${maxYear} 年之间。`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!currentUser && !editMode) {
      setError("无法获取当前用户信息，无法创建项目。");
      return;
    }

    setSubmitting(true);
    setError(null);

    // --- 创建模式下的固定或默认值 ---
    const creationDefaults = {
      report_template_id: 1, // 默认模板ID为1
      user_id: currentUser?.id, // 默认当前用户ID
      status: 'new', // 默认状态为'new'
      evaluation_type: '事后评价', // 默认评价类型
      project_type: projectType, // 确保项目类型被包含在默认值中
    };

    // --- 最终提交的数据 ---
    const projectPayload = {
      project_name: projectName,
      contract_year: contractYear.trim() || new Date().getFullYear().toString(),
      industry_type: industryType,
      client_level: clientLevel,
      project_type: projectType, // 添加项目类型
    };
    
    try {
      if (editMode && projectDataToEdit?.id) {
        // 编辑模式：只发送可编辑的字段
        const response = await apiClient.put(`/projects/${projectDataToEdit.id}`, projectPayload);
        
        if (response.success) {
          showSuccessMessage('项目更新成功！');
        } else {
          throw new Error(response.message || '更新项目失败');
        }

      } else {
        // 创建模式：合并可编辑字段和默认值
        const finalPayload = { ...projectPayload, ...creationDefaults };
        const response = await apiClient.projects.create(finalPayload);

        if (!response.success) {
          // 后端已做配额检查，这里按消息触发购买弹窗
          const msg = response.message || '创建项目失败';
          if (/配额|额度|未开通|套餐/.test(msg)) {
            onClose();
            window.dispatchEvent(new CustomEvent('showPaymentDialog', {
              detail: {
                reason: 'quota_exceeded',
                message: msg
              }
            }));
            return;
          }
          throw new Error(msg);
        }
        
        showSuccessMessage(response.message || '项目创建成功！');
      }

      if (onProjectCreated) {
        onProjectCreated();
      }
      onClose();

    } catch (err: any) {
      let errorMessage = '操作失败，请稍后重试。';
      try {
        // 尝试解析后端返回的JSON错误信息
        const errorObj = JSON.parse(err.message);
        if (errorObj && errorObj.error) {
          errorMessage = errorObj.error;
        } else {
          errorMessage = err.message;
        }
      } catch (parseError) {
        // 如果解析失败，说明错误信息不是JSON，直接使用
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      showErrorMessage(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editMode ? '编辑项目' : '创建新项目'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          autoFocus
          margin="dense"
          label="项目名称 *"
          type="text"
          fullWidth
          variant="outlined"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="合同年度"
          type="text"
          fullWidth
          variant="outlined"
          value={contractYear}
          onChange={(e) => setContractYear(e.target.value)}
          placeholder="例如：2024"
          error={!!(contractYear && contractYear.trim() && !isValidContractYear(contractYear))}
          helperText={contractYear && contractYear.trim() && !isValidContractYear(contractYear) ? 
            `合同年度必须在 ${new Date().getFullYear() - 10} 到 ${new Date().getFullYear() + 10} 年之间` : 
            '可选，格式如：2024'}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth margin="dense" sx={{ mb: 2 }} required>
          <InputLabel>行业类型 *</InputLabel>
          <Select
            value={industryType}
            label="行业类型 *"
            onChange={(e) => setIndustryType(e.target.value)}
          >
            {INDUSTRY_TYPES.map((type: string) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense" sx={{ mb: 2 }} required>
          <InputLabel>客户级别 *</InputLabel>
          <Select
            value={clientLevel}
            label="客户级别 *"
            onChange={(e) => setClientLevel(e.target.value)}
          >
            {CLIENT_LEVELS.map((level: string) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} color="secondary" disabled={submitting}>取消</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={submitting}>
          {submitting ? <CircularProgress size={24} color="inherit" /> : (editMode ? '保存更改' : '创建')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
